<template>
  <div class="lab-form-page">
    <div class="form-hero">
      <p class="form-crumb">
        Laboratorios / {{ form.id ? 'Editar' : 'Nuevo' }}
      </p>
      <h1 class="form-title">
        Laboratorio / {{ form.id ? 'Editar' : 'Nuevo' }}
      </h1>
      <p class="form-subtitle">
        Complete la información del laboratorio para asociarlo a uno o más
        distribuidores.
      </p>
    </div>

    <div class="form-card">
      <v-form @submit.prevent="submit">
        <!-- ===== Información del Laboratorio ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Información del Laboratorio</h3>

          <div class="form-grid">
            <div class="form-field">
              <label class="form-label">
                Nombre del Laboratorio <span class="form-required">*</span>
              </label>
              <v-text-field
                v-model="form.name"
                v-validate="'required'"
                :error-messages="errors.collect('name')"
                data-vv-name="name"
                placeholder="Ingrese el nombre del laboratorio"
                solo
                flat
                class="form-input"
              ></v-text-field>
            </div>

            <div class="form-field">
              <label class="form-label">
                Número de Teléfono <span class="form-required">*</span>
              </label>
              <v-text-field
                v-model="form.phone"
                v-validate="'required'"
                :error-messages="errors.collect('phone')"
                data-vv-name="phone"
                placeholder="Ingrese el número de teléfono"
                append-icon="phone"
                solo
                flat
                class="form-input"
              ></v-text-field>
            </div>

            <div class="form-field form-field--full">
              <label class="form-label">
                Dirección <span class="form-required">*</span>
              </label>
              <v-textarea
                v-model="form.address"
                placeholder="Ingrese la dirección completa del laboratorio"
                append-icon="place"
                solo
                flat
                rows="3"
                auto-grow
                class="form-input"
              ></v-textarea>
            </div>
          </div>
        </section>

        <div class="form-divider"></div>

        <!-- ===== Notificaciones ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Notificaciones</h3>

          <div class="form-toggle-row">
            <v-switch
              v-model="form.notificationsEnabled"
              hide-details
              color="#e5252a"
              class="form-switch"
            ></v-switch>
            <div class="form-toggle-text">
              <p class="form-toggle-label">
                Habilitar correos para notificaciones
              </p>
              <p class="form-toggle-help">
                Active esta opción si desea asignar correos para recibir
                notificaciones del sistema.
              </p>
            </div>
          </div>

          <div
            v-if="form.notificationsEnabled"
            class="form-field form-field--full"
          >
            <label class="form-label form-label--inline">
              Emails para Notificaciones
              <v-icon small class="form-label__hint">info</v-icon>
            </label>
            <v-combobox
              v-model="form.emails"
              placeholder="Ingrese los correos electrónicos separados por comas"
              append-icon="mail"
              chips
              multiple
              solo
              flat
              no-data-text="Datos no disponibles"
              class="form-input form-input--combobox"
            ></v-combobox>
            <p class="form-hint">
              Ejemplo: notificaciones@laboratorio.com, info@laboratorio.com
            </p>
          </div>
        </section>

        <div class="form-divider"></div>

        <!-- ===== Distribuidores Asociados ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Distribuidores Asociados</h3>

          <div class="form-field form-field--full">
            <label class="form-label">
              Distribuidores <span class="form-required">*</span>
            </label>
            <v-select
              v-model="form.distributors"
              v-validate="'required'"
              :error-messages="errors.collect('distributors')"
              :items="labOptions"
              item-text="name"
              item-value="id"
              data-vv-name="distributors"
              placeholder="Seleccione uno o más distribuidores"
              multiple
              chips
              deletable-chips
              solo
              flat
              no-data-text="Datos no disponibles"
              class="form-input form-input--select"
            ></v-select>
            <p class="form-hint">
              Puede asociar este laboratorio a varios distribuidores.
            </p>
          </div>
        </section>

        <div class="form-divider"></div>

        <!-- ===== Contacto ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Contacto</h3>

          <div class="form-grid">
            <div class="form-field">
              <label class="form-label">
                Nombre <span class="form-required">*</span>
              </label>
              <v-text-field
                v-model="form.contact.firstName"
                v-validate="'required'"
                :error-messages="errors.collect('contactFirstName')"
                data-vv-name="contactFirstName"
                data-vv-as="nombre"
                placeholder="Nombre del contacto"
                solo
                flat
                class="form-input"
              ></v-text-field>
            </div>

            <div class="form-field">
              <label class="form-label">
                Apellido <span class="form-required">*</span>
              </label>
              <v-text-field
                v-model="form.contact.lastName"
                v-validate="'required'"
                :error-messages="errors.collect('contactLastName')"
                data-vv-name="contactLastName"
                data-vv-as="apellido"
                placeholder="Apellido del contacto"
                solo
                flat
                class="form-input"
              ></v-text-field>
            </div>

            <div class="form-field">
              <label class="form-label">
                Correo del Contacto <span class="form-required">*</span>
              </label>
              <v-text-field
                v-model="form.contact.email"
                v-validate="'required|email'"
                :error-messages="errors.collect('contactEmail')"
                data-vv-name="contactEmail"
                data-vv-as="correo del contacto"
                type="email"
                placeholder="correo@laboratorio.com"
                append-icon="mail"
                solo
                flat
                class="form-input"
              ></v-text-field>
            </div>

            <div class="form-field">
              <label class="form-label">
                Contraseña
                <span v-if="!form.id" class="form-required">*</span>
              </label>
              <v-text-field
                v-model="form.contact.password"
                v-validate="form.id ? '' : 'required|min:6'"
                :error-messages="errors.collect('contactPassword')"
                data-vv-name="contactPassword"
                data-vv-as="contraseña"
                :type="showPassword ? 'text' : 'password'"
                :append-icon="showPassword ? 'visibility_off' : 'visibility'"
                :placeholder="
                  form.id
                    ? 'Dejar vacío para no cambiarla'
                    : 'Ingrese una contraseña'
                "
                solo
                flat
                class="form-input"
                @click:append="showPassword = !showPassword"
              ></v-text-field>
              <p v-if="form.id" class="form-hint">
                Deje este campo vacío si no desea cambiar la contraseña.
              </p>
            </div>
          </div>
        </section>

        <!-- ===== Footer ===== -->
        <div class="form-footer">
          <v-btn class="btn-cancel" flat @click="cancel">
            <v-icon left small>close</v-icon>Cancelar
          </v-btn>
          <v-btn type="submit" class="btn-save" :loading="saving">
            <v-icon left small>save</v-icon>
            {{ form.id ? 'Actualizar' : 'Guardar' }} Laboratorio
          </v-btn>
        </div>
      </v-form>
    </div>

    <v-dialog max-width="500" v-if="dialog.message" v-model="dialog.show">
      <message
        :key="dialog.key"
        :options="dialog"
        v-on:close="dialog.show = false"
      ></message>
    </v-dialog>
  </div>
</template>

<script>
import message from './../dialog/Message'

export default {
  components: { message },
  data() {
    return {
      saving: false,
      showPassword: false,
      distributors: [],
      form: {
        name: '',
        phone: '',
        address: '',
        emails: [],
        notificationsEnabled: true,
        distributors: [],
        contact: {
          firstName: '',
          lastName: '',
          email: '',
          password: '',
        },
      },
      dialog: { show: false, key: 0 },
    }
  },
  computed: {
    labOptions() {
      return this.distributors
    },
  },
  created() {
    const distPromise = this.$store
      .dispatch('distributors/getAll', {})
      .then(response => {
        const data = (response && response.items) || []
        this.distributors = data.map(d => ({
          name: d.name,
          id: d.id,
          laboratories: d.laboratories || [],
        }))
      })

    if ('id' in this.$route.params) {
      const labPromise = this.$store
        .dispatch('laboratories/getById', { id: this.$route.params.id })
        .then(response => {
          if (!response) {
            this.$router.push('/laboratorios/listado')
            return null
          }
          return response
        })

      // Esperamos AMBAS promesas para asegurarnos de que distributors esté
      // cargado antes de setear distributorId en el v-select
      Promise.all([distPromise, labPromise]).then(([, labResponse]) => {
        if (!labResponse) return

        const emails = Array.isArray(labResponse.emails)
          ? labResponse.emails
          : labResponse.emails
          ? labResponse.emails.split(',')
          : []

        // Los distribuidores asociados llegan bajo la clave `distribuidor`
        // (arreglo de objetos con id, name, ...). De ahí extraemos los IDs.
        const distArr =
          labResponse.distribuidor || labResponse.distributors || []
        let distributors = distArr
          .map(d => (d && d.id != null ? d.id : d))
          .filter(id => id != null)
        // Compatibilidad con el viejo campo único `distributorId`
        if (!distributors.length && labResponse.distributorId != null) {
          distributors = [labResponse.distributorId]
        }

        // El contacto puede venir bajo distintas claves según el backend
        const contactSrc =
          labResponse.contact || labResponse.contacto || labResponse.user || {}

        this.form = Object.assign({}, this.form, {
          id: labResponse.id,
          name: labResponse.name,
          phone: labResponse.phone,
          address: labResponse.address,
          emails,
          notificationsEnabled: emails.length > 0,
          distributors,
          contact: {
            firstName: contactSrc.firstName || '',
            lastName: contactSrc.lastName || '',
            email: contactSrc.email || '',
            // Vacío: en edición significa "no cambiar la contraseña"
            password: '',
          },
        })
      })
    }
  },
  methods: {
    submit() {
      this.$validator.validateAll().then(result => {
        if (!result) return

        this.saving = true

        const payload = {
          name: this.form.name,
          phone: this.form.phone,
          address: this.form.address,
          emails: this.form.notificationsEnabled ? [...this.form.emails] : [],
          distributors: [...this.form.distributors],
          contact: {
            firstName: this.form.contact.firstName,
            lastName: this.form.contact.lastName,
            email: this.form.contact.email,
            // En edición, password vacío => el backend no la cambia
            password: this.form.contact.password,
          },
        }
        // El id solo se incluye en edición (define la ruta en el API)
        if (this.form.id) payload.id = this.form.id

        this.$store
          .dispatch('laboratories/save', payload)
          .then(response => {
            this.saving = false
            if (response.status === 200) {
              if (this.form.id) {
                this.dialog = {
                  message: 'Los datos fueron actualizados exitosamente',
                  color: 'primary',
                  show: true,
                  key: this.dialog.key + 1,
                }
              } else {
                this.$router.push('/laboratorios/listado')
              }
            } else {
              this.dialog = {
                message:
                  (response.data && response.data.error) ||
                  'Ocurrió un error al guardar',
                color: 'error',
                show: true,
                key: this.dialog.key + 1,
              }
            }
          })
          .catch(() => {
            this.saving = false
            this.dialog = {
              message: 'No se pudo conectar al servidor. Intenta más tarde.',
              color: 'error',
              show: true,
              key: this.dialog.key + 1,
            }
          })
      })
    },
    cancel() {
      this.$router.push('/laboratorios/listado')
    },
  },
}
</script>

<style scoped>
.lab-form-page {
  padding: 24px 28px 40px;
  color: #172133;
  background: #f9fafb;
  min-height: 100%;
}

/* === Hero === */
.form-hero {
  margin-bottom: 24px;
}

.form-crumb {
  margin: 0 0 6px;
  font-size: 12.5px;
  color: #94a3b8;
  letter-spacing: 0.02em;
}

.form-title {
  margin: 0 0 6px;
  font-size: 26px;
  font-weight: 700;
  color: #172133;
  letter-spacing: -0.01em;
}

.form-subtitle {
  margin: 0;
  font-size: 13.5px;
  color: #64748b;
}

/* === Card === */
.form-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 32px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 14px rgba(15, 23, 42, 0.05);
}

.form-section__title {
  margin: 0 0 18px;
  font-size: 14px;
  font-weight: 700;
  color: #172133;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  position: relative;
  padding-bottom: 10px;
}

.form-section__title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 32px;
  height: 2px;
  background: #e5252a;
  border-radius: 1px;
}

.form-divider {
  height: 1px;
  background: #eef2f7;
  margin: 24px 0;
}

/* === Grid === */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 18px;
}

.form-field--full {
  grid-column: 1 / -1;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  letter-spacing: 0.01em;
}

.form-label__hint {
  color: #94a3b8 !important;
  font-size: 14px !important;
}

.form-required {
  color: #e5252a;
  font-weight: 700;
}

.form-hint {
  margin: 6px 0 0;
  font-size: 11.5px;
  color: #94a3b8;
}

/* === Toggle === */
.form-toggle-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 6px 0 14px;
}

.form-switch {
  margin: 0 !important;
  padding: 0 !important;
}

.form-toggle-text {
  flex: 1;
  padding-top: 6px;
}

.form-toggle-label {
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: #172133;
}

.form-toggle-help {
  margin: 2px 0 0;
  font-size: 11.5px;
  color: #64748b;
}

/* === Footer === */
.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #eef2f7;
}

.btn-cancel {
  border: 1px solid #e2e8f0 !important;
  background: transparent !important;
  color: #475569 !important;
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: 0.01em;
  font-weight: 500 !important;
  height: 42px !important;
  padding: 0 18px !important;
  box-shadow: none !important;
}

.btn-cancel:hover {
  background: #f8fafc !important;
  color: #172133 !important;
  border-color: #cbd5e1 !important;
}

.btn-save {
  background: #e5252a !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: 0.01em;
  font-weight: 600 !important;
  height: 42px !important;
  padding: 0 22px !important;
  box-shadow: 0 1px 2px rgba(229, 37, 42, 0.12),
    0 4px 12px rgba(229, 37, 42, 0.2) !important;
  transition: all 0.15s ease !important;
}

.btn-save:hover {
  background: #c91e23 !important;
  box-shadow: 0 2px 4px rgba(229, 37, 42, 0.18),
    0 6px 16px rgba(229, 37, 42, 0.28) !important;
  transform: translateY(-1px);
}

/* === Laboratorios asociados al distribuidor === */
.dist-labs-box {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
}

.dist-labs-box__title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 10px;
  font-size: 12.5px;
  font-weight: 600;
  color: #334155;
}

.dist-labs-box__icon {
  color: #64748b !important;
}

.dist-labs-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dist-labs-list__item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #475569;
}

.dist-labs-list__icon {
  color: #94a3b8 !important;
  font-size: 16px !important;
}

@media (max-width: 720px) {
  .lab-form-page {
    padding: 18px 14px 28px;
  }
  .form-card {
    padding: 18px 16px;
  }
  .form-title {
    font-size: 22px;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .form-footer {
    flex-direction: column-reverse;
  }
  .btn-cancel,
  .btn-save {
    width: 100%;
    justify-content: center;
  }
}
</style>

<style>
/* === Inputs (no scoped: v-text-field renderiza con clases globales) === */
.lab-form-page
  .form-input.v-text-field.v-text-field--solo
  .v-input__control
  > .v-input__slot {
  background: #ffffff !important;
  border: 1px solid #d1d5db !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
  min-height: 44px !important;
  padding: 0 14px !important;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    background 0.18s ease;
}

.lab-form-page
  .form-input.v-text-field.v-text-field--solo:hover
  .v-input__control
  > .v-input__slot {
  border-color: #9ca3af !important;
}

.lab-form-page
  .form-input.v-text-field.v-text-field--solo.v-input--is-focused
  .v-input__control
  > .v-input__slot {
  background: #ffffff !important;
  border-color: #254f87 !important;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12),
    0 1px 2px rgba(15, 23, 42, 0.04) !important;
}

.lab-form-page .form-input.error--text .v-input__control > .v-input__slot {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12) !important;
}

.lab-form-page .form-input input,
.lab-form-page .form-input textarea {
  color: #172133 !important;
  font-size: 14px !important;
  caret-color: #172133 !important;
}

.lab-form-page .form-input input::placeholder,
.lab-form-page .form-input textarea::placeholder {
  color: #94a3b8 !important;
  opacity: 1;
}

.lab-form-page .form-input .v-input__icon .v-icon {
  color: #94a3b8 !important;
  font-size: 19px !important;
}

.lab-form-page .form-input .v-messages.error--text {
  color: #e5252a !important;
  font-size: 11.5px !important;
  margin-top: 4px;
  padding-left: 4px;
}

/* Chips dentro del combobox */
.lab-form-page .form-input .v-chip {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #334155;
  font-weight: 500;
  margin: 4px 4px 4px 0 !important;
}

.lab-form-page .form-input .v-chip__close.v-icon {
  color: #94a3b8 !important;
}

/* Switch estilo iOS */
.lab-form-page .form-switch .v-input--switch__track {
  background: #e5e7eb !important;
  opacity: 1 !important;
  height: 22px !important;
  width: 40px !important;
  border-radius: 11px !important;
  top: calc(50% - 11px) !important;
  transition: background-color 0.25s ease;
}

.lab-form-page .form-switch.v-input--is-label-active .v-input--switch__track {
  background: #e5252a !important;
  opacity: 1 !important;
}

.lab-form-page .form-switch .v-input--switch__thumb {
  background: #ffffff !important;
  width: 18px !important;
  height: 18px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.06) !important;
  top: calc(50% - 9px) !important;
}
</style>
